import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { Avatar } from '../chat/Avatar'
import {
  getAgentShareTargets,
  revokeAgentShare,
  shareAgent,
  shareErrText,
  type AgentShareIdentity,
} from '../transport/relay'
import type { AgentRow } from '../transport/api'
import { AGENT_SHARES_KEY, namesLine, shareLeft, useAgentShares } from '../friends/agentShares'

/**
 * SHARE THIS AGENT — owner, 2026-10-17: 在進入 Share 的選單裡, 有選項讓使用者可以選擇要分享給哪些好友.
 * 並且可以設定要分享多久. 預設分享一天 … 也可以選擇永久分享. 使用者可以選擇讓自己的好友是以好友的名稱
 * 進入 Session 發言. 或是, 隱藏名稱, 使用使用者的名稱發言 … 隨時可以收回.
 *
 * His rulings on the design: the conversation from BEFORE the friend accepts is an option, off
 * unless chosen; so are the files in the agent's working folder (可以選擇讓好友看到 Agent working
 * folder 裡面的檔案, 預設看不到); and one warning line — the friend can ask the agent to do
 * anything it can do on this machine. The rows are the agent card's own (簡單簡潔): a short label each, nothing else.
 *
 * The friends are listed by an opaque account handle from the cloud, never by address, because
 * an address is not an identity (two accounts can share one).
 */
export function ShareForm({ agent, deviceId, machine }: { agent: AgentRow; deviceId: string; machine: string }) {
  const qc = useQueryClient()
  const targets = useQuery({ queryKey: ['agent-share-targets'], queryFn: getAgentShareTargets })
  const shares = useAgentShares()
  const friends = targets.data?.friends ?? []
  const mine = (shares.data?.outgoing ?? []).filter(
    (s) => s.device_id === deviceId && s.session === agent.session && s.status !== 'expired',
  )
  const sharedTo = new Set(mine.map((s) => s.account))
  const fresh = friends.filter((f) => !sharedTo.has(f.account))

  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [duration, setDuration] = useState<'1d' | 'forever'>('1d')
  const [identity, setIdentity] = useState<AgentShareIdentity>('friend')
  const [history, setHistory] = useState(false)
  const [files, setFiles] = useState(false)
  // 小鍵盤 B (owner, 2026-10-17): the keys row is an option, off like the files.
  const [keys, setKeys] = useState(false)
  // Pictures of its Chrome tabs (owner, 2026-10-17) — an option, off like the files.
  const [tabs, setTabs] = useState(false)
  const nowS = Date.now() / 1000

  const share = useMutation({
    mutationFn: () =>
      shareAgent({
        device_id: deviceId,
        session: agent.session,
        accounts: [...picked],
        duration,
        identity,
        history,
        files,
        keys,
        tabs,
      }),
    onSuccess: () => setPicked(new Set()),
    onSettled: () => void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY }),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => revokeAgentShare(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY }),
  })

  const toggle = (account: string) =>
    setPicked((p) => {
      const n = new Set(p)
      if (n.has(account)) n.delete(account)
      else n.add(account)
      return n
    })
  const who = namesLine(
    fresh.filter((f) => picked.has(f.account)).map((f) => f.name || f.email || 'They'),
    'Your friend',
  )

  return (
    <div className="ash">
      {mine.length > 0 && (
        <>
          <div className="ash-head">Shared with</div>
          <div className="ac-menu" data-testid="share-current">
            {mine.map((s) => {
              const name = s.friend.name || s.friend.email || 'A friend'
              return (
                <div className="ac-row ash-current" key={s.share_id}>
                  <Avatar picture={null} label={name} email={s.friend.email ?? ''} className="fr-pic-sm" />
                  {/* Two lines, so a long name or "until you take it back" never squeezes Revoke. */}
                  <span className="ac-row-label ash-who">
                    <span className="ash-name">{name}</span>
                    <span className="ash-when">
                      {s.status === 'pending' ? 'Waiting for an answer · ' : ''}
                      {shareLeft(s.expires_at, nowS, 'you')}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="ac-act ash-revoke"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(s.share_id)}
                  >
                    Revoke
                  </button>
                </div>
              )
            })}
          </div>
          {revoke.isError && <p className="ac-row-err">{shareErrText(revoke.error)}</p>}
        </>
      )}

      <div className="ash-head">Share with</div>
      {targets.isPending && <p className="ac-model-why">Reading…</p>}
      {targets.isError && <p className="ac-row-err">{shareErrText(targets.error)}</p>}
      {!targets.isPending && !targets.isError && fresh.length === 0 && (
        <p className="ac-model-why">
          {friends.length === 0 ? 'No friends yet — add one from the ＋ button.' : 'Shared with every friend.'}
        </p>
      )}
      {fresh.length > 0 && (
        <div className="ac-menu" data-testid="share-pick">
          {fresh.map((f) => {
            const name = f.name || f.email || 'A friend'
            const on = picked.has(f.account)
            return (
              <button
                type="button"
                className="ac-row"
                key={f.account}
                aria-pressed={on}
                onClick={() => toggle(f.account)}
              >
                <Avatar picture={f.picture ?? null} label={name} email={f.email ?? ''} className="fr-pic-sm" />
                <span className="ac-row-label">{name}</span>
                {on && <Icon name="check" size={18} className="pt-check" />}
              </button>
            )
          })}
        </div>
      )}

      {fresh.length > 0 && (
        <>
          <div className="ac-menu ash-opts">
            <div className="ac-row ash-opt">
              <span className="ac-row-label">How long</span>
              <span className="ash-seg" role="group" aria-label="How long">
                <button type="button" aria-pressed={duration === '1d'} onClick={() => setDuration('1d')}>
                  1 day
                </button>
                <button type="button" aria-pressed={duration === 'forever'} onClick={() => setDuration('forever')}>
                  Forever
                </button>
              </span>
            </div>
            <div className="ac-row ash-opt">
              <span className="ac-row-label">They speak as</span>
              <span className="ash-seg" role="group" aria-label="They speak as">
                <button type="button" aria-pressed={identity === 'friend'} onClick={() => setIdentity('friend')}>
                  Themselves
                </button>
                <button type="button" aria-pressed={identity === 'owner'} onClick={() => setIdentity('owner')}>
                  Me
                </button>
              </span>
            </div>
            <label className="ac-row ac-row-switch">
              <span className="ac-row-label">Show earlier conversation</span>
              <input
                type="checkbox"
                className="ac-switch"
                checked={history}
                onChange={(e) => setHistory(e.target.checked)}
                aria-label="Show the conversation from before they accept"
              />
            </label>
            {/* The setting hides the TRANSCRIPT, not the agent's memory (verification round 2) —
                said once, where the choice is made. */}
            {!history && <p className="ash-note">The agent itself still remembers it, and can tell them if asked.</p>}
            <label className="ac-row ac-row-switch">
              <span className="ac-row-label">Show files in its folder</span>
              <input
                type="checkbox"
                className="ac-switch"
                checked={files}
                onChange={(e) => setFiles(e.target.checked)}
                aria-label="Show the files in this agent's working folder"
              />
            </label>
            <label className="ac-row ac-row-switch">
              <span className="ac-row-label">Show its Chrome tabs</span>
              <input
                type="checkbox"
                className="ac-switch"
                checked={tabs}
                onChange={(e) => setTabs(e.target.checked)}
                aria-label="Show pictures of the Chrome tabs this agent holds"
              />
            </label>
            {tabs && (
              <p className="ash-note">
                They see pictures of the Chrome tabs this agent holds — whatever is on screen in them. A tab you
                pause is hidden from them.
              </p>
            )}
            <label className="ac-row ac-row-switch">
              <span className="ac-row-label">Give them the extra keys</span>
              <input
                type="checkbox"
                className="ac-switch"
                checked={keys}
                onChange={(e) => setKeys(e.target.checked)}
                aria-label="Give them the extra keys row"
              />
            </label>
            {/* What the keys do is not obvious from their labels — said where they are given.
                THIS SENTENCE IS THE CONSENT (avg-devapp-appstore, 2026-10-17): a friend able to put
                someone's agent into editing without asking is what App Review expects to be agreed
                to knowingly, and turning this on after reading it is that agreement. Do not shorten
                it to "extra keys"; agentShare.test.tsx holds it. */}
            {keys && (
              <p className="ash-note">
                Tab, ⇧Tab, / and the arrows go straight into its terminal — ⇧Tab can switch it to making edits
                without asking you.
              </p>
            )}
          </div>
          <p className="ash-warn" role="note">
            <Icon name="warning" size={14} />
            <span>
              {who} can ask this agent to do anything it can do on {machine}.
            </span>
          </p>
          <button
            type="button"
            className="sheet-btn primary ash-go"
            disabled={picked.size === 0 || share.isPending}
            onClick={() => share.mutate()}
          >
            {share.isPending ? 'Sharing…' : 'Share'}
          </button>
          {share.isError && <p className="ac-row-err">{shareErrText(share.error)}</p>}
        </>
      )}
    </div>
  )
}
